import { memo, useCallback, useState } from 'react';
import { cn, Cross, Popup } from '@divlab/divanui';

import * as Profile from '@Queries/Profile';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import useRouter from '@Navigation/useRouter';
import Link from '@Navigation/Link';
import useOnClickOutside from '@Hooks/useOnClickOutside';
import CounterPoints from '@Components/CounterPoints';
import BonusPopup from '@Components/BonusPopup';
import styles from './OrderBonuses.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface OrderBonusesProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  count: number;
  error?: string;
  loading?: boolean;
}

const OrderBonuses: FC<OrderBonusesProps> = (props) => {
  const { className, count = 0, error, loading, ...restProps } = props;
  const { t } = useTranslation();
  const [visiblePopup, setVisiblePopup] = useState(false);
  const profile = Profile.useProfile();
  const router = useRouter();
  const { country } = useRequest();
  const isRus = country === 'RUS';

  const bonusText = profile.data ? t('ui.bonuses.text.auth') : t('ui.bonuses.text.not-auth');

  const handleClickBonuses = useCallback(() => {
    setVisiblePopup((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setVisiblePopup(false);
  }, []);

  const refPopup = useOnClickOutside<HTMLDivElement>(handleClose);

  if (loading) {
    return <div className={styles.loader}>{t('ui.bonuses.count')}</div>;
  }

  return (
    <div {...restProps} className={cn(styles.bonuses, className)}>
      {error ? (
        <div className={styles.error}>{error}</div>
      ) : (
        <div className={styles.wrapper} ref={refPopup}>
          <div className={styles.iconPoints} />
          <CounterPoints
            className={styles.count}
            prefixMode={isRus ? 'max' : 'plus'}
            count={count}
            onClick={handleClickBonuses}
            data-testid='coupon'
          />

          {isRus ? (
            <BonusPopup className={styles.bonusPopup} visible={visiblePopup} />
          ) : (
            <Popup className={styles.popup} visible={visiblePopup}>
              <Cross className={styles.iconClose} size='xs' onClick={handleClose} />
              <>
                {bonusText}
                <Link className={styles.link} to={router.club} target='_blank' view='native'>
                  {t('ui.bonuses.about-PL')}
                </Link>
              </>
            </Popup>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(OrderBonuses);
